import { useSessionContext } from '@/contexts/session-context';
import { signOut } from '@/lib/auth-client';
import { routes } from '@/lib/routes';
import {
  ArrowLeft,
  FileText,
  Flag,
  LayoutDashboard,
  Loader2,
  LogOut,
  type LucideIcon,
  Monitor,
  Shield,
  Tags,
  Users
} from 'lucide-react';
import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';

interface NavItem {
  href: string;
  labelKey: string;
  icon: LucideIcon;
}

// Static nav items - labels resolved at render time
const NAV_ITEMS: NavItem[] = [
  { href: routes.admin.dashboard, labelKey: 'admin.nav.dashboard', icon: LayoutDashboard },
  { href: routes.admin.users, labelKey: 'admin.nav.users', icon: Users },
  { href: routes.admin.reports, labelKey: 'admin.nav.reports', icon: Flag },
  { href: routes.admin.letters, labelKey: 'admin.nav.letters', icon: FileText },
  { href: routes.admin.interests, labelKey: 'admin.nav.interests', icon: Tags },
  { href: routes.admin.sessions, labelKey: 'admin.nav.sessions', icon: Monitor }
];

function isNavActive(href: string, pathname: string): boolean {
  return pathname === href || (href !== routes.admin.dashboard && pathname.startsWith(href));
}

// Memoized nav link component
const AdminNavLink = memo(function AdminNavLink({
  href,
  labelKey,
  icon: Icon,
  isActive,
  variant
}: NavItem & { isActive: boolean; variant: 'desktop' | 'mobile' }) {
  const { t } = useTranslation();

  if (variant === 'mobile') {
    return (
      <Link
        to={href}
        className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg transition-all ${
          isActive ? 'text-blue' : 'text-sepia/50'
        }`}
      >
        <Icon className="w-5 h-5" />
        <span className="text-xs">{t(labelKey)}</span>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
        isActive ? 'bg-blue/10 text-blue font-medium' : 'text-sepia/70 hover:bg-white'
      }`}
    >
      <Icon className={`w-5 h-5 ${isActive ? 'text-blue' : ''}`} />
      <span>{t(labelKey)}</span>
    </Link>
  );
});

// Memoized sidebar navigation
const AdminSidebarNav = memo(function AdminSidebarNav({ pathname }: { pathname: string }) {
  return (
    <aside className="w-56 shrink-0 hidden lg:block">
      <nav className="sticky top-24 space-y-1">
        {NAV_ITEMS.map((item) => (
          <AdminNavLink
            key={item.href}
            {...item}
            isActive={isNavActive(item.href, pathname)}
            variant="desktop"
          />
        ))}
      </nav>
    </aside>
  );
});

// Memoized mobile navigation
const AdminMobileNav = memo(function AdminMobileNav({ pathname }: { pathname: string }) {
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-warm px-2 py-2 flex justify-around">
      {NAV_ITEMS.slice(0, 5).map((item) => (
        <AdminNavLink
          key={item.href}
          {...item}
          isActive={isNavActive(item.href, pathname)}
          variant="mobile"
        />
      ))}
    </nav>
  );
});

// Memoized header component
const AdminHeader = memo(function AdminHeader({
  userEmail,
  onSignOut
}: {
  userEmail: string;
  onSignOut: () => void;
}) {
  const { t } = useTranslation();

  return (
    <header className="border-b border-gray-warm bg-white sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={routes.inbox}
            className="flex items-center gap-2 text-sepia/70 hover:text-sepia transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">{t('admin.backToApp')}</span>
          </Link>
          <div className="h-6 w-px bg-gray-warm" />
          <Link
            to={routes.admin.dashboard}
            className="flex items-center gap-2 text-xl font-semibold text-ink"
          >
            <Shield className="w-5 h-5 text-blue" />
            <span>{t('admin.title')}</span>
          </Link>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-sepia/70">{userEmail}</span>
          <button
            type="button"
            onClick={onSignOut}
            className="p-2 rounded-lg hover:bg-cream transition-colors"
            title={t('nav.signOut')}
          >
            <LogOut className="w-5 h-5 text-sepia/70" />
          </button>
        </div>
      </div>
    </header>
  );
});

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isPending } = useSessionContext();

  useEffect(() => {
    if (!isPending && !user) {
      navigate(routes.login);
    }
    // Redirect non-admin users
    if (!isPending && user && user.role !== 'ADMIN') {
      navigate(routes.inbox);
    }
  }, [user, isPending, navigate]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate(routes.login);
  }, [navigate]);

  if (isPending) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sepia animate-spin" />
      </div>
    );
  }

  if (!user || user.role !== 'ADMIN') {
    return null;
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader userEmail={user.email} onSignOut={handleSignOut} />

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        <AdminSidebarNav pathname={location.pathname} />

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>

      <AdminMobileNav pathname={location.pathname} />
    </div>
  );
}
